import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API } from "../utils/apk";

const Dailysale = () => {
    const today = new Date();
    const defaultDate = today.toISOString().split("T")[0];

    const [date, setDate] = useState(defaultDate);
    const [month, setMonth] = useState("");
    const [reportData, setReportData] = useState([]);

    useEffect(() => {
        const fetchReport = async () => {
            try {
                let queryParam = "";

                if (month) {
                    queryParam = `month=${month}`;
                } else if (date) {
                    queryParam = `date=${date}`;
                }

                const response = await fetch(`${API}/get/daily/monthly/yearly/report?${queryParam}`);
                const result = await response.json();

                if (result.success) {
                    setReportData(result.data);
                } else {
                    setReportData([]);
                    console.error("No delivered orders found.");
                }
            } catch (err) {
                console.error("Error fetching report:", err);
            }
        };

        fetchReport();
    }, [date, month]);

    const handleDateChange = (e) => {
        setDate(e.target.value);
    };

    const handleMonthChange = (e) => {
        setMonth(e.target.value);
    };

    const grandTotal = reportData.reduce(
        (total, order) => total + (order.orderId?.totalPrice || 0),
        0
    );

    const exportToExcel = () => {
        const excelData = [];

        reportData.forEach((order) => {
            const items = order.orderId?.addToCart?.cartIds || [];
            items.forEach((item, index) => {
                excelData.push({
                    "Order ID": index === 0 ? order.orderId?.orderId : "",
                    "Item Name": item.itemName,
                    "Item Rate": item.itemRate,
                    Quantity: item.count,
                    "Customer Name": index === 0 ? order.orderId?.userAddress?.name : "",
                    "Customer Mobile": index === 0 ? order.orderId?.userAddress?.mobile : "",
                    "D.Boy Name": index === 0 ? order.deliveryBoyId?.name : "",
                    "D.Boy Number": index === 0 ? order.deliveryBoyId?.mobileNo : "",
                    "Total Price": index === 0 ? order.orderId?.totalPrice : "",
                });
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sale Report");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });

        const filename = month
            ? `Monthly_Sale_Report_${month}.xlsx`
            : `Daily_Sale_Report_${date}.xlsx`;

        saveAs(fileData, filename);
    };

    return (
        <div className="p-6 bg-blue-200 min-h-screen">
            <h2 className="text-center text-xl font-bold text-white bg-yellow-800 py-2 rounded-t-md px-6">
                SALE REPORT
            </h2>

            <div className="p-2 flex justify-end items-center gap-4">
                {/* Date Picker */}
                <input
                    type="date"
                    value={date}
                    onChange={handleDateChange}
                    className="text-black px-2 py-1 rounded w-36 h-10"
                />

                {/* Month Dropdown */}
                <select
                    value={month}
                    onChange={handleMonthChange}
                    className="text-black px-2 py-1 rounded w-36 h-10"
                >
                    <option value="">Select Month</option>
                    <option value="january">January</option>
                    <option value="february">February</option>
                    <option value="march">March</option>
                    <option value="april">April</option>
                    <option value="may">May</option>
                    <option value="june">June</option>
                    <option value="july">July</option>
                    <option value="august">August</option>
                    <option value="september">September</option>
                    <option value="october">October</option>
                    <option value="november">November</option>
                    <option value="december">December</option>
                </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto mt-0 bg-white rounded shadow">
                <div className="max-h-[400px] overflow-y-auto">
                    <table className="min-w-full text-sm text-left">
                        <thead className="bg-gray-200 font-semibold sticky top-0 z-10">
                            <tr>
                                {[
                                    "Order ID", "Item Name", "Item Rate", "Quantity",
                                    "Customer Name", "Customer Mobile",
                                    "D.Boy Name", "D.Boy Number", "Total Price"
                                ].map((header) => (
                                    <th key={header} className="px-4 py-2 sticky top-0 bg-gray-200 z-20">{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((order, idx) => {
                                const items = order.orderId?.addToCart?.cartIds || [];
                                const rowspan = items.length;
                                const colorClasses = [
                                    "bg-yellow-100",
                                    "bg-blue-100",
                                    "bg-green-100",
                                    "bg-pink-100",
                                    "bg-purple-100",
                                ];
                                const rowColor = colorClasses[idx % colorClasses.length];

                                return items.map((item, itemIdx) => (
                                    <tr key={`${order._id}-${itemIdx}`} className={`${rowColor} border-b`}>
                                        {itemIdx === 0 && (
                                            <td rowSpan={rowspan} className="px-4 py-2 font-medium">
                                                {order.orderId?.orderId}
                                            </td>
                                        )}
                                        <td className="px-4 py-2">{item.itemName}</td>
                                        <td className="px-4 py-2">{item.itemRate}</td>
                                        <td className="px-4 py-2">{item.count}</td>
                                        {itemIdx === 0 && (
                                            <>
                                                <td rowSpan={rowspan} className="px-4 py-2">
                                                    {order.orderId?.userAddress?.name}
                                                </td>
                                                <td rowSpan={rowspan} className="px-4 py-2">
                                                    {order.orderId?.userAddress?.mobile}
                                                </td>
                                                <td rowSpan={rowspan} className="px-4 py-2">
                                                    {order.deliveryBoyId?.name}
                                                </td>
                                                <td rowSpan={rowspan} className="px-4 py-2">
                                                    {order.deliveryBoyId?.mobileNo}
                                                </td>
                                                <td rowSpan={rowspan} className="px-4 py-2 font-semibold">
                                                    ₹{order.orderId?.totalPrice}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center mt-4 px-4">
                <div className="font-semibold text-lg">
                    Grand Total: <span className="font-bold text-green-700">₹{grandTotal.toFixed(2)}</span>
                </div>
                <button
                    onClick={exportToExcel}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
                >
                    Export to Excel
                </button>
            </div>
        </div>
    );
};

export default Dailysale;
